<template>
  <div class="text-center">
    <v-btn
      color="primary"
      @click="dialog = true"
    >
      New Trial
    </v-btn>

    <v-dialog
      v-model="dialog"
      persistent
      width="auto"
    >
      <v-card>
        <v-card-title>
          <span>New Trial</span>
        </v-card-title>
        <v-card-text>
          <v-container>
          <v-form ref="form" lazy-validation>
            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-text-field
                  label="School name*"
                  required
                  v-model="trial.school_name"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-combobox 
                  label="District"
                  v-model="trial.district_name"
                  variant="outlined"
                  :items="districts"
                  required
                  :rules="[rules.required]"
                >
                </v-combobox>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-text-field
                  v-model="trial.firstname"
                  label="First name*"
                  required
                  hint="Enter first name"
                  :rules="[rules.required]"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-text-field
                  v-model="trial.lastname"
                  label="Last name*"
                  hint="Enter last name"
                  clearable
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  label="Email*"
                  v-model="trial.email"
                  :rules="[rules.required, rules.email]" 
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  label="Phone*"
                  v-model="trial.phone_number"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  v-model="trial.username"
                  label="Username*"
                  required
                  autocomplete="off"
                  :name="'trial-username-' + Math.random().toString(36).substring(2)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  v-model="trial.password"
                  label="Password*"
                  type="password"
                  required
                  autocomplete="off"
                  :name="'trial-password-' + Math.random().toString(36).substring(2)"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
              <v-select
                v-model="trial.timezone"
                :items="timeZones"
                item-text="text"
                item-value="value"
                label="Time Zone*"
                :rules="[rules.required]"
              ></v-select>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
              <v-text-field
                v-model.number="trial.num_students"
                label="Student Enrollment"
                :rules="[rules.required, rules.number]"
                type="number"
                step="1"
                data-cy="trial-student-enrollment"
              ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
          </v-container>
          <small>*indicates required field</small>
          </v-card-text>
        <v-card-actions >
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            variant="flat" 
            @click="validate(trial)"
            :disabled="isLoading"
            :loading="isLoading"
          >
            {{ isLoading ? 'Saving...' : 'Save Trial' }}
          </v-btn>
          <v-btn color="primary" variant="outlined" @click="dialog = false" >Cancel</v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>
  </div>
</template>
<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.v-card-title{
  background-color: $primary;
  color: white;
}

</style>
<script setup lang="ts">
// import Autocomplete from "@/components/autocomplete.vue";
import type { Trial } from "@/api_types/school";
import { useAlertsStore } from "@/stores/alerts";
import useSchools from "@/stores/support/schools";
import useTrials from "@/stores/support/trials";
import { ref } from "vue";
import { computed } from "vue";

const alertStore = useAlertsStore();
const schoolStore = useSchools();
const trialsStore = useTrials();
const form = ref();
const trial = ref<Trial>({
  school_name: '',
  district_name: '',
  firstname: '',
  lastname: '',
  email: '',
  phone_number: '',
  username: '',
  password: '',
  num_students: 0,
  timezone: ''
});
const dialog = ref(false);
const isLoading = ref(false);
const usZones = [
  { CountryCode: "US", Timezone: "America/New_York", Description: "Eastern Time" },
  { CountryCode: "US", Timezone: "America/Chicago", Description: "Central Time" },
  { CountryCode: "US", Timezone: "America/Denver", Description: "Mountain Time" },
  { CountryCode: "US", Timezone: "America/Los_Angeles", Description: "Pacific Time" },
  { CountryCode: "US", Timezone: "America/Anchorage", Description: "Alaska Time" },
  { CountryCode: "US", Timezone: "Pacific/Honolulu", Description: "Hawaii Time" }
];
const districts = computed(() => schoolStore.districts.data.map(district => (district.district_name)));

const timeZones = ref(usZones.map(zone => (zone.Timezone )));
const rules = {
        required: value => !!value || 'Field is required',
        email: value => /.+@.+/.test(value) || 'Invalid email address', 
        number: value => !isNaN(parseFloat(value)) && isFinite(value) || 'Must be a number', 
      };
const createTrial = async function(trial: Trial){
  console.log("Creating trial");
  isLoading.value = true;
  try {
    const res = await trialsStore.createTrial(trial);
    console.log(res);
    if (res.status){
      alertStore.addAlert("success", "New Trial Created", 5);
      dialog.value = false;
      // Reset form
      trial.school_name = '';
      trial.district_name = '';
      trial.firstname = '';
      trial.lastname = '';
      trial.email = '';
      trial.phone_number = '';
      trial.username = '';
      trial.password = '';
      trial.num_students = 0;
      trial.timezone = '';
    }else{
      alertStore.addAlert("error", res.message, 10);
    }
  } catch (error) {
    console.error("Error creating trial:", error);
    alertStore.addAlert("error", "Failed to create trial", 10);
  } finally {
    isLoading.value = false;
  }
};
const validate = async (trial: Trial)=>{
        const { valid } = await form.value.validate()
        if (valid){
          createTrial(trial);
        }else{
          console.log("invalid form"); 
        }
      };
</script>